import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Compass3D } from '../decor/compass/Compass3D'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import { cartesianCircleGeometry as geometry } from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoAssetModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoTankFlatRoof,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 A surface representing a circular tank roof, plotted on a cartesian plane,
 with the center of its base at "position" and decorated with Rich Asset
 features specific to flat roofs.
 * @param model               The tank roof component model
 * @param assetModel          The asset model for the asset that the roof is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */

export const FlatRoofSurface3D: React.FC<
  {
    model: GeckoTankFlatRoof
    assetModel: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, assetModel, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()
  const {
    bounds,
    scale,
    manways,
    rectangularManways,
    nozzles,
    verticalWelds,
    horizontalWelds,
    plates,
  } = React.useMemo(
    () => ({
      bounds: model.componentBounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(
          model.radius.scale(2.0).num,
          model.radius.scale(2.0).num,
          1.0
        )
      ),
      manways: model.manwaysArray,
      rectangularManways: model.rectangularManwaysArray,
      nozzles: model.nozzlesArray,
      verticalWelds: model.verticalWeldsArray,
      horizontalWelds: model.horizontalWeldsArray,
      plates: model.platesArray,
    }),
    [model, groupProps.scale]
  )

  const getCompassPosition = React.useCallback<(angle: number) => Vec3>(
    (angle) => {
      const radians = (angle * Math.PI) / 180
      return [-0.55 * Math.sin(radians), -0.55 * Math.cos(radians), 0]
    },
    []
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.start.x)
        .scale(1.0 / model.componentBounds.width.num).num
      return [relX - 0.5, -0.6, 0] // offset ruler by -1
    },
    [model.componentBounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.start.y)
        .scale(1.0 / model.componentBounds.height.num).num
      return [-0.6, relY - 0.5, 0] // offset ruler by -1
    },
    [model.componentBounds]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}
      componentModel={model}>
      <TankDecorGroup3D yPivot={show3D}>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
      <Ruler3D
        anchor={'top'}
        start={model.componentBounds.start.x}
        end={model.componentBounds.end.x}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionXTick}
      />
      <Compass3D
        assetModel={assetModel}
        componentModel={model}
        getRotationPosition={getCompassPosition}
        radial
      />
      <FeatureDispatch
        features={[
          manways,
          rectangularManways,
          nozzles,
          verticalWelds,
          horizontalWelds,
          plates,
        ]}
      />
    </ModelSurface3D>
  )
}
